// ---------------------------------------------------------------------------
// TITLE-CASE PT-BR — desengrita nome/cidade que o CRM legado guarda em CAIXA
// ALTA ("JOY XANGRI-LÁ", "CAPÃO DA CANOA").
//
// REGRA DE ENTRADA: só age quando o texto está 100% em maiúsculas. Qualquer
// minúscula presente = o cadastro já foi escrito com capitalização própria e
// NÃO se mexe ("Residencial Bela Vista" fica exatamente assim).
//
// Uso restrito à página do empreendimento (title/description gerada/H1/OG/
// JSON-LD). O uppercase dos CARDS é estilo da casa e não passa por aqui.
// ---------------------------------------------------------------------------

// Preposições/conjunções que ficam minúsculas — exceto quando abrem o texto.
const MINUSCULAS = new Set(['de', 'da', 'do', 'dos', 'das', 'e', 'em']);

// Siglas de UF: têm vogal e passariam por palavra comum ("BA" -> "Ba").
const UFS = new Set([
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]);

const VOGAIS = /[AEIOUÁÀÂÃÉÊÍÓÔÕÚÜ]/;
// II, III, IV, VI, IX, XI, XII... — numeral romano curto de torre/bloco.
const ROMANO = /^[IVX]{2,3}$/;

/** Está 100% em maiúsculas? (precisa ter ao menos uma letra) */
export const ehCaixaAlta = (texto: string | null | undefined): boolean => {
    const valor = String(texto || '');
    if (!/[a-zA-ZÀ-ÿ]/.test(valor)) return false;
    return valor === valor.toUpperCase() && valor !== valor.toLowerCase();
};

/**
 * Preserva o token como veio quando ele NÃO é palavra comum:
 *  • tem dígito ("463", "2A", "G1");
 *  • é sigla de UF ("RS", "SP");
 *  • é numeral romano curto ("II", "IV");
 *  • tem 2–3 letras SEM vogal ("JK", "CDA") — assinatura de sigla.
 * O corte por vogal é o que separa sigla de palavra: "JK" fica "JK", mas
 * "SOL"/"MAR"/"JOY" viram "Sol"/"Mar"/"Joy" (senão o título seguiria gritado,
 * que é justamente o que este helper existe pra resolver).
 */
const preservaToken = (token: string): boolean => {
    if (/\d/.test(token)) return true;
    if (UFS.has(token)) return true;
    if (ROMANO.test(token)) return true;
    return token.length >= 2 && token.length <= 3 && !VOGAIS.test(token);
};

// Capitaliza respeitando hífen e apóstrofo: XANGRI-LÁ -> Xangri-Lá,
// D'AVILA -> D'Avila. Cada pedaço é avaliado de novo (LÁ tem vogal -> Lá).
const capitalizaPedacos = (palavra: string): string =>
    palavra
        .split(/([-'’])/)
        .map((pedaco) => {
            if (/^[-'’]$/.test(pedaco) || !pedaco) return pedaco;
            if (preservaToken(pedaco)) return pedaco;
            return pedaco.charAt(0).toUpperCase() + pedaco.slice(1).toLowerCase();
        })
        .join('');

export const titleCasePt = (texto: string | null | undefined): string => {
    const valor = String(texto || '').trim();
    if (!valor) return '';
    // Cadastro já capitalizado à mão: devolve intacto.
    if (!ehCaixaAlta(valor)) return valor;

    // Preserva os espaços originais (inclusive duplos) ao remontar.
    const partes = valor.split(/(\s+)/);
    let indicePalavra = 0;
    return partes
        .map((parte) => {
            if (/^\s+$/.test(parte) || !parte) return parte;
            const posicao = indicePalavra;
            indicePalavra += 1;
            if (preservaToken(parte)) return parte;
            const minuscula = parte.toLowerCase();
            // preposição minúscula, menos quando abre o texto
            if (posicao > 0 && MINUSCULAS.has(minuscula)) return minuscula;
            return capitalizaPedacos(parte);
        })
        .join('');
};
